import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sun, Mail, Lock, User, Phone, ArrowRight, Loader2, Car } from 'lucide-react';
import { Toaster, toast } from 'sonner';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'customer'
  });
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigateByRole(userData.role);
      } else {
        const userData = await register(
          formData.name,
          formData.email,
          formData.password,
          formData.role,
          formData.phone
        );
        toast.success('Account created successfully!');
        navigateByRole(userData.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (role) => {
    switch (role) {
      case 'driver':
        navigate('/driver');
        break;
      case 'admin':
        navigate('/admin');
        break;
      default:
        navigate('/book');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      <Toaster position="top-right" theme="dark" />
      
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_santorini-booking/artifacts/b01x660c_FullLogo%20%281%29.png" 
              alt="Santorini Best Experience"
              className="h-14"
            />
          </div>
          
          <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
            Santorini<br />
            <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">Best Experience</span>
          </h1>
          
          <p className="text-xl text-zinc-300 mb-8 max-w-md">
            Premium transportation & tours in the most beautiful island of Greece. Airport transfers, wine tours, sunset experiences & more.
          </p>
          
          <div className="flex items-center gap-8 text-zinc-400">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">5000+</p>
              <p className="text-sm">Happy Guests</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">4.9★</p>
              <p className="text-sm">Rating</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">24/7</p>
              <p className="text-sm">Service</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_santorini-booking/artifacts/b01x660c_FullLogo%20%281%29.png" 
              alt="Santorini Best Experience"
              className="h-12"
            />
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? 'Welcome back' : 'Create account'}
              </h2>
              <p className="text-zinc-400">
                {isLogin ? 'Sign in to book your experience' : 'Start your Santorini adventure'}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="pl-12 h-12 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                        required={!isLogin}
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        type="tel"
                        placeholder="+30 XXX XXX XXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="pl-12 h-12 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300 mb-2 block">I want to</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, role: 'customer'})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.role === 'customer'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid="role-customer-btn"
                      >
                        <User className="w-6 h-6 mx-auto mb-2" />
                        <p className="font-medium">Book Rides</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, role: 'driver'})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.role === 'driver'
                            ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                            : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid="role-driver-btn"
                      >
                        <Car className="w-6 h-6 mx-auto mb-2" />
                        <p className="font-medium">Be a Driver</p>
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <Label className="text-zinc-300 mb-2 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-12 h-12 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-300 mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pl-12 h-12 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-semibold"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center mt-6 text-zinc-400">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-amber-400 hover:text-amber-300 font-medium"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
          
          <p className="text-center mt-6 text-zinc-500 text-sm">
            <Link to="/" className="text-amber-400 hover:text-amber-300">← Back to Home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
